"use client";
import { cn } from "@aichat/ui/lib/utils";
import { LucideProps } from "lucide-react";
import { useState } from "react";
import UserIcon from "../icons/UserIcon";

type AssistantAvatarProps = LucideProps & {
  variant?: "light" | "dark";
  avatarType: "bot" | "live-agent";
  customUrl?: string;
  customUrlClassName?: string;
};

function AssistantAvatar({
  variant,
  customUrl,
  customUrlClassName,
  ...rest
}: AssistantAvatarProps) {
  const [error, setError] = useState(false);
  if (customUrl) {
    if (error)
      return (
        <UserIcon
          className={cn("rounded-full size-5 object-cover", customUrlClassName)}
        />
      );
    return (
      <img
        src={customUrl}
        onError={() => setError(true)}
        alt="Assistant Avatar"
        className={cn("rounded-full size-5 object-cover", customUrlClassName)}
      />
    );
  }
  return <Icon {...rest} variant={variant} />;
}

AssistantAvatar.displayName = "AssistantAvatar";

type AssistantAvatarWithBgProps = {
  containerProps?: React.HTMLProps<HTMLDivElement>;
  avatarProps?: AssistantAvatarProps;
  brandHSLAString?: string;
  avatarType: "bot" | "live-agent";
};

function AssistantAvatarWithBg({
  containerProps,
  avatarProps,
  brandHSLAString,
}: AssistantAvatarWithBgProps) {
  if (avatarProps?.customUrl) {
    return <AssistantAvatar {...avatarProps} />;
  }
  return (
    <div
      className={cn(
        "bg-gradient-to-br from-primary-50 to-primary grid place-items-center",
        containerProps?.className,
      )}
      style={{
        background: brandHSLAString
          ? brandHSLAString
          : "background: linear-gradient(135deg, #A8F0AA 11.34%, #96ED9A 37.06%, #6EDE73 70.24%, #3FD447 88.31%)",
        ...containerProps?.style,
      }}
    >
      <AssistantAvatar
        {...avatarProps}
        avatarType={avatarProps?.avatarType ?? "bot"}
      />
    </div>
  );
}

AssistantAvatarWithBg.displayName = "AssistantAvatarWithBg";

export { AssistantAvatar, AssistantAvatarWithBg };

function Icon(props: AssistantAvatarProps) {
  const { avatarType, variant, ...rest } = props;
  if (avatarType === "bot") {
    if (variant === "dark")
      return (
        <svg
          viewBox="0 0 46 38"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          {...rest}
        >
          <path
            d="M41.9982 16.1317V29.3224C44.0919 29.3003 45.8 27.5702 45.8 25.4654V19.9886C45.8 17.8838 44.0919 16.1647 41.9982 16.1317Z"
            fill={rest.color ? rest.color : "black"}
          />
          <path
            d="M3.99081 29.3224V16.1317C1.88603 16.1647 0.199997 17.8838 0.199997 19.9886V25.4544C0.199998 27.5702 1.88603 29.2783 3.99081 29.3224Z"
            fill={rest.color ? rest.color : "black"}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M34.042 37.4428H11.947C7.81612 37.4428 5.82013 35.1271 5.82013 31.3269V17.4978C5.82013 13.6232 7.81612 11.3709 11.947 11.3709H22.0854V9.55265V9.10826C20.1349 8.70053 18.6692 6.9594 18.6692 4.88767C18.6692 2.49637 20.6087 0.556885 23 0.556885C25.3803 0.556885 27.3198 2.49637 27.3198 4.88767C27.3198 6.9594 25.8542 8.70053 23.9147 9.10826V9.55265V11.3709H34.042C37.9748 11.3709 40.1689 13.6232 40.1689 17.4978V31.3158C40.1689 35.46 38.1745 37.4428 34.042 37.4428ZM32.422 19.9994C32.422 18.3244 31.0555 16.958 29.3695 16.958C27.6834 16.958 26.317 18.3244 26.328 19.9994C26.328 21.6855 27.6945 23.0409 29.3695 23.0409C31.0555 23.0409 32.422 21.6855 32.422 19.9994ZM19.672 19.9994C19.672 18.3244 18.3166 16.958 16.6306 16.958C14.9335 16.958 13.5781 18.3244 13.5891 19.9994C13.5891 21.6855 14.9445 23.0409 16.6306 23.0409C18.3166 23.0409 19.672 21.6855 19.672 19.9994ZM16.5429 30.5174C18.5179 31.5324 20.7499 32.0684 22.9999 32.0684C25.2489 32.0684 27.4819 31.5324 29.4569 30.5174C29.9489 30.2644 30.1419 29.6614 29.8899 29.1704C29.6369 28.6794 29.0339 28.4854 28.5429 28.7374C25.1079 30.5034 20.8919 30.5034 17.4569 28.7374C16.9639 28.4854 16.3619 28.6804 16.1099 29.1704C15.8579 29.6624 16.0519 30.2644 16.5429 30.5174Z"
            fill={rest.color ? rest.color : "black"}
          />
        </svg>
      );
    else {
      return (
        <svg
          viewBox="0 0 46 37"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          {...rest}
        >
          <path
            d="M41.7982 15.5748V28.7655C43.8919 28.7435 45.6 27.0133 45.6 24.9086V19.4317C45.6 17.3269 43.8919 15.6078 41.7982 15.5748Z"
            fill={rest.color ? rest.color : "white"}
          />
          <path
            d="M3.79082 28.7655V15.5748C1.68603 15.6078 0 17.3269 0 19.4317V24.8975C1.152e-06 27.0133 1.68603 28.7214 3.79082 28.7655Z"
            fill={rest.color ? rest.color : "white"}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M33.842 36.8859H11.747C7.61612 36.8859 5.62013 34.5702 5.62013 30.77V16.9409C5.62013 13.0663 7.61612 10.814 11.747 10.814H21.8854V8.99576V8.55137C19.9349 8.14364 18.4692 6.40251 18.4692 4.33079C18.4692 1.93949 20.4087 0 22.8 0C25.1803 0 27.1198 1.93949 27.1198 4.33079C27.1198 6.40251 25.6542 8.14364 23.7147 8.55137V8.99576V10.814H33.842C37.7748 10.814 39.9689 13.0663 39.9689 16.9409V30.759C39.9689 34.9031 37.9746 36.8859 33.842 36.8859ZM32.222 19.4425C32.222 17.7675 30.8555 16.4011 29.1695 16.4011C27.4834 16.4011 26.117 17.7675 26.128 19.4425C26.128 21.1286 27.4945 22.484 29.1695 22.484C30.8555 22.484 32.222 21.1286 32.222 19.4425ZM19.472 19.4425C19.472 17.7675 18.1166 16.4011 16.4306 16.4011C14.7335 16.4011 13.3781 17.7675 13.3891 19.4425C13.3891 21.1286 14.7445 22.484 16.4306 22.484C18.1166 22.484 19.472 21.1286 19.472 19.4425ZM16.3429 29.9605C18.3179 30.9755 20.5499 31.5115 22.7999 31.5115C25.0489 31.5115 27.2819 30.9755 29.2569 29.9605C29.7489 29.7075 29.9419 29.1045 29.6899 28.6135C29.4369 28.1225 28.8339 27.9285 28.3429 28.1805C24.9079 29.9465 20.6919 29.9465 17.2569 28.1805C16.7639 27.9285 16.1619 28.1235 15.9099 28.6135C15.6579 29.1055 15.8519 29.7075 16.3429 29.9605Z"
            fill={rest.color ? rest.color : "white"}
          />
        </svg>
      );
    }
  } else
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        className={rest.className}
      >
        <circle
          cx="12"
          cy="6"
          r="4"
          fill={rest.color ?? (variant === "dark" ? "black" : "white")}
        />
        <path
          fill={rest.color ?? (variant === "dark" ? "black" : "white")}
          d="M20 17.5c0 2.485 0 4.5-8 4.5s-8-2.015-8-4.5S7.582 13 12 13s8 2.015 8 4.5"
        />
      </svg>
    );
}
